import React from 'react';
import { connect } from 'react-redux';
import { Typography, withStyles } from '@material-ui/core';

import { capitalize } from '../../../lib/helpers';
import {
  getScoundScoreProperties,
  getTransportationProperties,
  getFloodScoreProperties,
} from '../../../lib/neighborhoodHelpers';
import ToriiLoader from '../../utility/ToriiLoader';
import NoResults from '../../utility/NoResults';
import { isMobile, isTablet } from '../../../lib/getDeviceSize';
import DonutComponent from '../../utility/Donut';
import PriceTrendText from './PriceTrendText';
import RentTrendText from './RentTrendText';
import PricePointText from './PricePointText';
import PriceAndRentChart from '../tools/trend-spotter/PriceAndRentChart';
import { getPricePointerData, getTrends } from '../../../actions/listing';
import styles from './styles';

const DONUT_SIZE = isMobile() ? 60 : 80;
const DONUT_STROKE_WIDTH = isMobile() ? 6 : 8;

interface Props {
  classes: any;
  getPricePointerData: Function;
  getTrends: Function;
  listing: any;
  loading: boolean;
  neighborhood: any;
  pricePoints: any;
  trends: any;
  showInModal?: boolean;
}

export class Neighborhood extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.props.getTrends(
      this.props.listing.zipCode,
      this.props.listing.noBedrooms,
    );
    this.props.getPricePointerData(this.props.listing.zipCode);
  }

  _renderTransportationScore = (transportationScore: any) => {
    if (!transportationScore) {
      return null;
    }
    const { classes } = this.props;
    const displayScore = (type: string, score: number | undefined) => {
      const {
        trackColor,
        indicatorColor,
        possibility,
        description,
      } = getTransportationProperties(type, score);
      return (
        <div
          className={`${classes.individualScoreContainer} ${classes.extraPadder}`}
        >
          <DonutComponent
            size={DONUT_SIZE}
            strokeWidth={DONUT_STROKE_WIDTH}
            trackColor={trackColor}
            indicatorColor={indicatorColor}
            value={score ? score : 0}
            label={
              score ? (score === 100 ? `${score}` : `${score}/100`) : 'N/A'
            }
          />
          <Typography className={classes.scoreText}>{possibility}</Typography>
          <Typography className={classes.scoreDescriptionText}>
            {description}
          </Typography>
          <Typography className={classes.scoreTrademarkText}>
            {capitalize(type)} Score®
          </Typography>
        </div>
      );
    };

    return (
      <div className={classes.scoreContainer}>
        <h3 className={classes.scoreTitle}>Transportation Score</h3>
        <Typography className={classes.scoreProviderText}>
          Data provided by{' '}
          <a
            href="https://www.walkscore.com/"
            rel="noopener noreferrer"
            target="_blank"
            style={{ textDecorationLine: 'underline', color: '#555555' }}
          >
            Walk Score
          </a>
        </Typography>
        <div className={classes.scoreContentContainer}>
          {displayScore('walk', transportationScore.walkScore)}
          {displayScore('transit', transportationScore.transitScore)}
          {displayScore('bike', transportationScore.bikeScore)}
        </div>
      </div>
    );
  };

  _renderSoundScore = (score: any) => {
    const { classes } = this.props;
    const { donutColors, description } = getScoundScoreProperties(score);
    if (!score) {
      return null;
    }
    return (
      <div className={classes.scoreContainer}>
        <h3 className={classes.scoreTitle}>Sound Score</h3>
        <Typography className={classes.scoreProviderText}>
          Data provided by{' '}
          <a
            href="https://howloud.com/"
            rel="noopener noreferrer"
            style={{ textDecorationLine: 'underline', color: '#555555' }}
            target="_blank"
          >
            HowLoud
          </a>
        </Typography>

        <div className={classes.scoreContentContainer}>
          <div className={classes.individualScoreContainer}>
            <DonutComponent
              size={DONUT_SIZE}
              strokeWidth={DONUT_STROKE_WIDTH}
              trackColor={donutColors.trackColor}
              indicatorColor={donutColors.indicatorColor}
              value={score ? score : 0}
              label={
                score ? (score === 100 ? `${score}` : `${score}/100`) : 'N/A'
              }
            />
            <Typography className={classes.scoreText}>{description}</Typography>
          </div>
        </div>
      </div>
    );
  };

  _renderFloodScore = (score: any) => {
    const { classes } = this.props;
    const { donutColors, possibility, description } = getFloodScoreProperties(
      score,
    );
    if (!score) {
      return null;
    }
    return (
      <div className={classes.scoreContainer}>
        <h3 className={classes.scoreTitle}>Flood Score</h3>
        <Typography className={classes.scoreProviderText}>
          Data provided by{' '}
          <a
            href="https://floodfactor.com/"
            rel="noopener noreferrer"
            style={{ textDecorationLine: 'underline', color: '#555555' }}
            target="_blank"
          >
            FloodFactor
          </a>
        </Typography>

        <div className={classes.scoreContentContainer}>
          <div className={classes.individualScoreContainer}>
            <DonutComponent
              size={DONUT_SIZE}
              strokeWidth={DONUT_STROKE_WIDTH}
              trackColor={donutColors.trackColor}
              indicatorColor={donutColors.indicatorColor}
              value={score ? score * 10 : 0}
              label={score ? `${score}/10` : 'N/A'}
            />
            <Typography className={classes.scoreText}>{possibility}</Typography>
            <Typography className={classes.scoreDescriptionText}>
              {description}
            </Typography>
          </div>
        </div>
      </div>
    );
  };

  _renderMarketTrends = ({ showTrends, showPricePoints }: any) => {
    const { classes, listing, pricePoints, trends } = this.props;
    return showTrends || showPricePoints ? (
      <div className={classes.marginContainer}>
        <h3 className={classes.scoreTitle} style={{ textAlign: 'left' }}>
          Market Trends for {listing.zipCode}
        </h3>
        <Typography
          className={classes.scoreProviderText}
          style={{ textAlign: 'left' }}
        >
          Data provided by Torii
        </Typography>
        <div style={{ marginTop: 15 }}>
          {showTrends && (
            <React.Fragment>
              <PriceTrendText data={trends} />
              <RentTrendText data={trends} />
            </React.Fragment>
          )}
          {showPricePoints && <PricePointText data={pricePoints} />}
          {showTrends && (
            <div style={{ marginTop: 50 }}>
              <PriceAndRentChart data={trends} width={800} height={300} />
            </div>
          )}
        </div>
      </div>
    ) : null;
  };

  render() {
    const { classes, neighborhood, loading } = this.props;
    const { pricePoints, trends, listing } = this.props;
    const showTrends = trends.length > 0;
    const showPricePoints = pricePoints.length > 0;
    return (
      <div className={classes.neighborhoodContainer}>
        <div className={`row ${classes.marginContainer}`}>
          <h1 className={classes.detailsTitleBig}>Neighborhood Info</h1>
        </div>
        {loading ? (
          <ToriiLoader padTop={50} />
        ) : !neighborhood ? (
          <div>
            <NoResults
              emptyText="Oops! It looks like we couldn’t find a neighborhood score"
              bottomText
              padTop
            />
          </div>
        ) : (
          <div style={{ marginTop: 25 }}>
            {this._renderTransportationScore(neighborhood.transportScores)}
            {this._renderSoundScore(neighborhood.soundScore)}
            {this._renderFloodScore(neighborhood.floodScore)}
            {this._renderMarketTrends({ showTrends, showPricePoints })}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  loading: state.listings.neighborhoodLoading,
  neighborhood: state.listings.neighborhood,
  pricePoints: state.listings.pricePoints,
  trends: state.listings.trends,
});
const mapDispatchToProps = (dispatch: any) => ({
  getPricePointerData(zipCode: string) {
    dispatch(getPricePointerData(zipCode));
  },
  getTrends(zipCode: string, noBedrooms: number) {
    dispatch(getTrends(zipCode, noBedrooms));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(Neighborhood));
